import {isNull, uniqBy} from 'lodash-es';
import {type AxiosRequestConfig} from 'axios';
import {asDecimalCurrency, asInteger} from 'utils/number-format';
import {cpServiceV2Post} from 'api';
import {type ApiResponse} from 'api/api.types';
import {fullMonthYearFormat} from 'utils/date-time';
import dayjs from 'utils/dayjs';
import {sendSentryException} from 'utils/error-handling';
import {
    type FormattedGraphData,
    type GetAccountingDashboardReportRawResponse,
    type GetAccountingDashboardReportSummary,
    type GetMtdAccountingReportBaseRequest,
    type MTDAggregatedReportRawResponse,
    type MTDAggregatedReport,
    type GetMTDFacilitiesBreakdownReportRawResponse,
} from 'api/accounting/accounting.types';
import {camelizeKeys, convertKeysToSnakeCase} from 'utils/format-strings';

/**
 * Formats MTD filters for the v2 JSON request body.
 * @group DTO Formatters
 */
export const formatMtdRequest = ({
    fromDate,
    toDate,
    facilityIds,
    perPage,
    page,
}: GetMtdAccountingReportBaseRequest) => {
    const formattedRequest = convertKeysToSnakeCase({
        fromDate,
        toDate,
        facilityIds,
        perPage,
        page,
    });

    return formattedRequest;
};

/**
 * formats the revenue data for the MTD aggregated graph
 * @param  data - The response data to format
 * @param  lastMonth - The name of the previous month
 * @param  currentMonth - The name of the current month
 * @returns the revenue data formatted for the MTD aggregated graph
 *
 * @group DTO Formatters
 */
const formatMTDAggregatedRevenue = (
    data: MTDAggregatedReport[],
    lastMonth: string,
    currentMonth: string
) => {
    const formattedRevenueData: FormattedGraphData[] = data
        .reduce(
            (acc, revenueResponseData, index) => {
                const {
                    date: currentDateLabel,
                    previous: {
                        date: previousDateLabel,
                        remit: {mtd: previousRemitMTD},
                    },
                    remit: {mtd: currentRemitMTD},
                } = revenueResponseData;

                acc[0].data.push({
                    id: previousDateLabel,
                    x: index + 1,
                    y: previousRemitMTD ? previousRemitMTD / 100 : 0,
                });

                // As long as the data points for the current month DO NOT contain `null` values, add them to the data series for the current month.
                if (!isNull(currentRemitMTD)) {
                    acc[1].data.push({
                        id: currentDateLabel,
                        x: index + 1,
                        y: !isNull(currentRemitMTD)
                            ? currentRemitMTD / 100
                            : currentRemitMTD,
                    });
                }

                return acc;
            },
            [
                {id: lastMonth, data: []},
                {id: currentMonth, data: []},
            ]
        )
        .map((dataSet) => ({
            ...dataSet,
            data: uniqBy(dataSet.data, 'id'),
        }));

    return formattedRevenueData;
};

/**
 * formats the reservations data for the MTD aggregated graph
 * @param data - The response data to format
 * @param lastMonth - The name of the previous month
 * @param currentMonth - The name of the current month
 * @returns the reservation data formatted for the MTD aggregated graph
 *
 * @group DTO Formatters
 */
const formatMTDAggregatedReservations = (
    data: MTDAggregatedReport[],
    lastMonth: string,
    currentMonth: string
) => {
    const formattedReservationsData: FormattedGraphData[] = data
        .reduce(
            (acc, reservationsResponseData, index) => {
                const {
                    date: currentDateLabel,
                    previous: {
                        date: previousDateLabel,
                        transactions: {mtd: previousTransactionsMTD},
                    },
                    transactions: {mtd: currentTransactionsMTD},
                } = reservationsResponseData;

                acc[0].data.push({
                    id: previousDateLabel,
                    x: index + 1,
                    y: previousTransactionsMTD || 0,
                });

                // As long as the data points for the current month DO NOT contain `null` values, add them to the data series for the current month.
                if (!isNull(currentTransactionsMTD)) {
                    acc[1].data.push({
                        id: currentDateLabel,
                        x: index + 1,
                        y: currentTransactionsMTD || 0,
                    });
                }

                return acc;
            },
            [
                {id: lastMonth, data: []},
                {id: currentMonth, data: []},
            ]
        )
        .map((dataSet) => ({
            ...dataSet,
            data: uniqBy(dataSet.data, 'id'),
        }));

    return formattedReservationsData;
};

/**
 * Formats the response from the dashboard endpoint
 * @param  response - The response data to format
 * @param  toDate - The toDate parameter representing the end of the date range queried
 *
 * @group DTO Formatters
 */
const formatMTDAggregatedReport = (
    response: MTDAggregatedReportRawResponse[],
    toDate: GetMtdAccountingReportBaseRequest['toDate']
) => {
    const formattedData = camelizeKeys(response);
    const endDate = dayjs(toDate);
    const currentMonth = endDate.format(fullMonthYearFormat);
    const lastMonth = endDate.subtract(1, 'month').format(fullMonthYearFormat);

    const formattedResponse = {
        revenue: formatMTDAggregatedRevenue(
            formattedData,
            lastMonth,
            currentMonth
        ),
        reservations: formatMTDAggregatedReservations(
            formattedData,
            lastMonth,
            currentMonth
        ),
    };

    return formattedResponse;
};

/**
 * Formats the response data for the dashboard MTD summary endpoint
 * @param  response - The data to format
 *
 * @group DTO Formatters
 */
export const formatGetMtdSummary = (
    response: GetAccountingDashboardReportRawResponse['mtd']
): GetAccountingDashboardReportSummary => {
    const formattedData = camelizeKeys(response);

    const formattedResponse = {
        revenue: {
            value: asDecimalCurrency(formattedData?.remit.mtd / 100),
            previousValue: asDecimalCurrency(
                (formattedData?.previous.remit.mtd || 0) / 100
            ),
            diff: formattedData?.remit.change,
        },
        reservations: {
            value: asInteger(formattedData?.transactions.mtd),
            previousValue: asInteger(
                formattedData?.previous.transactions.mtd || 0
            ),
            diff: formattedData?.transactions.change,
        },
    };

    return formattedResponse;
};

/**
 * Gets the dashboard (summary cards) accounting data for a collection of facilities
 * @param  data - The parameters for the report
 * @param config - The axios config
 *
 * @throws Propagates API errors, including HTTP 422 request validation failures.
 * @see '/control-panel-service/v2/dashboard'
 */
export const getMtdSummary = async (
    data: GetMtdAccountingReportBaseRequest,
    config: AxiosRequestConfig = {}
) => {
    try {
        const response =
            await cpServiceV2Post<GetAccountingDashboardReportRawResponse>(
                'dashboard',
                formatMtdRequest(data),
                config
            );

        const formattedResponse = formatGetMtdSummary(response.data.data.mtd);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

/**
 * Gets a collection of aggregated MTD reports for a given set of facilities
 * @param data - The parameters for the report
 * @param config - The axios config
 *
 * @throws Propagates API errors, including HTTP 422 request validation failures.
 * @see '/control-panel-service/v2/facilities/mtd/aggregated'
 */
export const getMTDAggregatedReport = async (
    data: GetMtdAccountingReportBaseRequest,
    config: AxiosRequestConfig = {}
) => {
    const {toDate} = data;

    try {
        const response = await cpServiceV2Post<
            MTDAggregatedReportRawResponse[]
        >('facilities/mtd/aggregated', formatMtdRequest(data), config);

        const formattedResponse = formatMTDAggregatedReport(
            response.data.data.results,
            toDate
        );

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

/**
 * Formats a given MTD (Month-To-Date) report response by camelizing the
 * keys and rounding the current rating values to one decimal place.
 *
 * @param response - The original response object to be formatted.
 * @returns The formatted response with camelized keys and rating values rounded to one decimal place.
 *
 * @group DTO Formatters
 */
const formatMTDReportResponse = (
    response: ApiResponse<GetMTDFacilitiesBreakdownReportRawResponse[]>['data']
) => {
    const formattedResponse = camelizeKeys(response);

    const formattedResults = formattedResponse.data.results.map((data) => ({
        ...data,
        currentRating: data.currentRating,
    }));

    const formattedData = {
        data: {
            results: formattedResults,
        },
        meta: formattedResponse.meta,
    };

    return formattedData;
};

/**
 * Gets an MTD (breakdown table) report for a given set of facilities
 * @param data - The parameters for the report
 * @param config - The axios config
 *
 * @throws Propagates API errors, including HTTP 422 request validation failures.
 * @see '/control-panel-service/v2/facilities/mtd'
 */
export const getMTDReport = async (
    data: GetMtdAccountingReportBaseRequest,
    config: AxiosRequestConfig = {}
) => {
    try {
        const response = await cpServiceV2Post<
            GetMTDFacilitiesBreakdownReportRawResponse[]
        >('facilities/mtd', formatMtdRequest(data), config);
        const formattedResponse = formatMTDReportResponse(response.data);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};
